import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { DeliveriesTable } from './Components';
import ConfirmPriceModal from './ConfirmPriceModal';
import { useSubmitOrderMutation } from './submitOrder.generated';
import { motion } from 'framer-motion';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  OrderSummaryWithCheckout,
  fToCurrency,
  groupByTaxRate,
  useLockBodyScroll,
} from '@web/utils';
import arrowTop from '@web/utils/src/assets/icons/functions/arrow-top.svg';
import plusIcon from '@web/utils/src/assets/icons/functions/plus-icon.svg';
import { calculateCartTotal } from '@web/utils/src/calculateCartTotal';
import { useCustomToast } from '@/hooks/useCustomToast';
import { cartAtom, userAtom } from '@/store/atoms';
import { clearCartAtom } from '@/store/store';
import { Currency, OrderDataInput } from '@/types';
import { removeIdsFromRetailerCart } from '@agrantis/cart';

const variants = {
  open: {
    y: 0,
    transition: { type: 'spring', stiffness: 100, damping: 10, mass: 0.5 },
  },
  closed: { y: '100vh', transition: { duration: 0.2 } },
};

const CheckoutPage = () => {
  const [isMobileSummaryOpen, setMobileSummaryOpen] = useState(false);
  const { retailerId = '1' } = useParams();
  const user = useAtomValue(userAtom);
  const cartItems = useAtomValue(cartAtom);
  const cart = cartItems[retailerId];
  const { orderTotal, hasZeroPricedItem } = calculateCartTotal(cart);
  const cleartCart = useSetAtom(clearCartAtom);

  const navigate = useNavigate();
  const showToast = useCustomToast();
  //
  const { taxesByPercentage, totalPercentes } = useMemo(
    () => groupByTaxRate(cart),
    [cart],
  );

  const [submitResult, submitOrder] = useSubmitOrderMutation();

  const handleCheckout = async () => {
    const mappedDeliveries = removeIdsFromRetailerCart(cart);

    const orderData: OrderDataInput = {
      currency: Currency.Eur,
      totalNet: orderTotal,
      deliveries: mappedDeliveries,
      billingAddress: {
        firstName: user?.givenName || '',
        lastName: user?.familyName || '',
        addressLine1: user?.primaryAddress.addressLine1 || '',
        addressLine2: user?.primaryAddress.addressLine2 || '',
        addressLocality: user?.primaryAddress.addressLocality || '',
        addressPostcode: user?.primaryAddress.addressPostcode || '',
        country: 'DE',
        company: '',
      },
    };

    try {
      const result = await submitOrder({
        retailerId,
        orderData,
      });
      if (result.data) {
        cleartCart({ retailerId });
        navigate(`/order/${result.data.createOrder.id}/success`);
      } else {
        showToast('error', t('checkout.error_placing_order'));
      }
    } catch (error) {
      console.log('SubmitResult error', { ...submitResult });
    }
  };

  useLockBodyScroll(isMobileSummaryOpen);
  const { t } = useTranslation();

  return (
    <div className="pb-15-0">
      <div className="grid grid-cols-1 md:grid-cols-[3fr_1fR] gap-x-4-0">
        <div className="md:hidden z-99 fixed bottom-0 left-0 w-full bg-[#FDF3CC] p-5-5">
          <div className="flex justify-between p-1-5 text-3-0 font-bd text-primary-black leading-1-5 items-center">
            <p>{t('checkout.total_gross')}</p>
            <p>{fToCurrency(orderTotal + totalPercentes)}</p>
            <img
              src={arrowTop}
              className="w-3-0 h-3-0"
              onClick={() => setMobileSummaryOpen(true)}
            />
          </div>
        </div>
        <div className="pt-5-0">
          {cart && <DeliveriesTable deliveries={cart} />}
        </div>
        <div className="w-[25%] mt-5-0 hidden md:block">
          <OrderSummaryWithCheckout
            taxesByPercentage={taxesByPercentage}
            totalPercentes={totalPercentes}
            orderTotal={orderTotal}
            handleOrderSubmit={handleCheckout}
            submitResult={submitResult}
            className="fixed w-full max-w-[35rem]"
            t={t}
            hasZeroPricedItem={hasZeroPricedItem}
          />
        </div>
      </div>
      <motion.div
        className="fixed bg-primary-white p-5-0 z-100 top-0 left-0 w-100-vw h-100-vh"
        initial="closed"
        animate={isMobileSummaryOpen ? 'open' : 'closed'}
        variants={variants}
      >
        <div className="flex justify-between mb-5-0">
          <p className="text-2-8 font-bd">{t('checkout.cart')}</p>
          <img
            src={plusIcon}
            className="transform rotate-45 w-4-0 h-4-0"
            onClick={() => setMobileSummaryOpen(false)}
          />
        </div>
        <OrderSummaryWithCheckout
          taxesByPercentage={taxesByPercentage}
          hasZeroPricedItem={hasZeroPricedItem}
          totalPercentes={totalPercentes}
          orderTotal={orderTotal}
          handleOrderSubmit={handleCheckout}
          submitResult={submitResult}
          t={t}
        />
      </motion.div>
    </div>
  );
};

export default CheckoutPage;
